import { useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, IconButton, Tooltip, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch,
    MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Property definīciju (classifiers.property_definitions) CRUD pārvaldība.
 *
 * Server endpoints (AdminClassifiersController.cs):
 *   POST /api/v1/admin/classifiers/properties/list
 *   POST /api/v1/admin/classifiers/properties/upsert
 *   POST /api/v1/admin/classifiers/properties/delete/{id}
 *
 * Tiek atspoguļotas visas definīcijas (gan active, gan inactive). Dzēšot tādas, kas jau
 * lietotas (places_properties), serveris automātiski pārslēdz Active=false, nevis dzēš.
 */
type PropertyItem = {
    id: string;
    name: string;
    orderNo: number | null;
    active: boolean;
    required: boolean;
    group: string | null;
    type: string | null;
};

type ListResponse = {
    isSuccess: boolean;
    error: string | null;
    items: PropertyItem[];
};

const PROPERTY_TYPES = ['text', 'boolean', 'integer', 'decimal', 'date'];

const EMPTY_DRAFT: PropertyItem = {
    id: '00000000-0000-0000-0000-000000000000',
    name: '',
    orderNo: null,
    active: true,
    required: false,
    group: '',
    type: 'boolean',
};


export function ClassifiersPage() {
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState<PropertyItem | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<PropertyItem | null>(null);

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-classifier-properties'],
        queryFn: async () => {
            const resp = await api.post<ListResponse>('/api/v1/admin/classifiers/properties/list', {});
            return resp.data;
        },
    });

    const upsertMutation = useMutation({
        mutationFn: async (item: PropertyItem) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null; id: string }>(
                '/api/v1/admin/classifiers/properties/upsert',
                {
                    id: item.id,
                    name: item.name,
                    orderNo: item.orderNo,
                    active: item.active,
                    required: item.required,
                    group: item.group || null,
                    type: item.type,
                },
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Saglabāšana neizdevās.');
            return resp.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-classifier-properties'] });
            setEditing(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                `/api/v1/admin/classifiers/properties/delete/${id}`,
                {},
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Dzēšana neizdevās.');
            return resp.data;
        },
        onSuccess: (resp) => {
            queryClient.invalidateQueries({ queryKey: ['admin-classifier-properties'] });
            setConfirmDelete(null);
            // Soft-delete notikums - serveris atgriež error tekstu kā warning, IsSuccess=true
            if (resp.error) alert(resp.error);
        },
    });

    const items = data?.items ?? [];

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Klasifikatori — Properties</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Vietas īpašību (property_definitions) pārvaldība. Tier konvencija:
                        order_no &le; 300 → populārās (SP redaktorā uzreiz redzamas),
                        &gt; 300 → paslēptas zem "Rādīt citus" pogas.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            upsertMutation.reset();
                            setEditing({ ...EMPTY_DRAFT });
                        }}
                    >
                        Pievienot
                    </Button>
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt: {String((error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nosaukums</TableCell>
                                <TableCell>Grupa</TableCell>
                                <TableCell>Tips</TableCell>
                                <TableCell align="right">Secība</TableCell>
                                <TableCell align="center">Tier</TableCell>
                                <TableCell align="center">Aktīvs</TableCell>
                                <TableCell align="right">Darbības</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((it) => {
                                const tier = (it.orderNo ?? 9999) <= 300 ? 1 : 2;
                                return (
                                    <TableRow key={it.id}>
                                        <TableCell>{it.name}</TableCell>
                                        <TableCell>{it.group ?? '—'}</TableCell>
                                        <TableCell>
                                            <Chip label={it.type ?? 'text'} size="small" />
                                        </TableCell>
                                        <TableCell align="right">{it.orderNo ?? '—'}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={`T${tier}`}
                                                size="small"
                                                color={tier === 1 ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {it.active
                                                ? <Chip label="Jā" size="small" color="success" />
                                                : <Chip label="Nē" size="small" />}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    upsertMutation.reset();
                                                    setEditing({ ...it });
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    deleteMutation.reset();
                                                    setConfirmDelete(it);
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {editing && (
                <PropertyEditDialog
                    item={editing}
                    onClose={() => setEditing(null)}
                    onSave={(item) => upsertMutation.mutate(item)}
                    busy={upsertMutation.isPending}
                    errorText={upsertMutation.error ? (upsertMutation.error as Error).message : null}
                />
            )}

            {confirmDelete && (
                <Dialog open onClose={() => setConfirmDelete(null)}>
                    <DialogTitle>Dzēst īpašību?</DialogTitle>
                    <DialogContent>
                        {deleteMutation.error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Dzēšana neizdevās: {(deleteMutation.error as Error).message}
                            </Alert>
                        )}
                        <Typography>
                            Vai tiešām dzēst <b>{confirmDelete.name}</b>?
                            Ja tā jau ir lietota vietās, tā tiks atzīmēta kā neaktīva,
                            nevis dzēsta.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDelete(null)}>Atcelt</Button>
                        <Button
                            color="error"
                            onClick={() => deleteMutation.mutate(confirmDelete.id)}
                            disabled={deleteMutation.isPending}
                        >
                            Dzēst
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}


/**
 * Modāls property definīcijas redaktors. Lieto gan create (Id=Empty), gan update režīmā.
 */
function PropertyEditDialog({
    item, onClose, onSave, busy, errorText,
}: {
    item: PropertyItem;
    onClose: () => void;
    onSave: (item: PropertyItem) => void;
    busy: boolean;
    errorText: string | null;
}) {
    const [draft, setDraft] = useState(item);
    const isNew = item.id === '00000000-0000-0000-0000-000000000000';

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isNew ? 'Jauna īpašība' : 'Rediģēt īpašību'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {errorText && (
                        <Alert severity="error">
                            Saglabāšana neizdevās: {errorText}
                        </Alert>
                    )}
                    <TextField
                        label="Nosaukums"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        autoFocus
                        fullWidth
                    />
                    <TextField
                        label="Grupa (group_name)"
                        value={draft.group ?? ''}
                        onChange={(e) => setDraft({ ...draft, group: e.target.value })}
                        fullWidth
                        helperText="Loģiska grupa (Accessibility, Hygiene, …) — kategorizē UI."
                    />
                    <TextField
                        label="Tips"
                        select
                        value={draft.type ?? 'text'}
                        onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                        fullWidth
                        helperText={
                            (draft.type ?? 'text') === 'boolean'
                                ? 'Tri-state: mobilais klients atļauj Jā / Nē / nezinām (tukšs). Filtrs un displejs ignorē neuzstādītās.'
                                : (draft.type ?? 'text') === 'date'
                                    ? 'ISO 8601 (YYYY-MM-DD) - mobilais klients atver MaterialDatePicker.'
                                    : undefined
                        }
                    >
                        {PROPERTY_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Secība (order_no)"
                        type="number"
                        value={draft.orderNo ?? ''}
                        onChange={(e) => setDraft({
                            ...draft,
                            orderNo: e.target.value === '' ? null : Number(e.target.value),
                        })}
                        fullWidth
                        helperText="≤ 300 = tier 1 (populārā, SP redz uzreiz); > 300 = tier 2 (zem 'Rādīt citus')."
                    />
                    <FormControlLabel
                        control={<Switch
                            checked={draft.active}
                            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                        />}
                        label="Aktīvs"
                    />
                    <FormControlLabel
                        control={<Switch
                            checked={draft.required}
                            onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                        />}
                        label="Obligāts"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave(draft)}
                    disabled={busy || !draft.name.trim()}
                >
                    Saglabāt
                </Button>
            </DialogActions>
        </Dialog>
    );
}
